import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import ModalTabSelector from '../components/ModalTabSelector';
import { ChevronLeft, ChevronRight, Calendar, CalendarDays, Sun, Clock, Package, Users, User, X, Check, CreditCard, Mail, Phone, Link2, ExternalLink, Loader2, ChevronDown, ChevronUp, Globe, Lock, Zap, Smartphone } from 'lucide-react';
import { collectInPerson, isTapToPaySupported } from '../utils/tapToPay';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import useMinChargeFloor from '../hooks/useMinChargeFloor';
import { toast } from 'sonner';
import TimeSlotSelector from '../components/TimeSlotSelector';
import DatePicker from '../components/DateRangePicker';
import ServiceCalendarPicker from '../components/ServiceCalendarPicker';
import SharePaymentLinkModal from '../components/SharePaymentLinkModal';
import PaymentMethodSelector from '../components/PaymentMethodSelector';
import GiftCardApplyBlock from '../components/GiftCardApplyBlock';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../utils/api';  // Keep for legacy endpoints (admin/bookings/create-with-checkout)
import { orgApi } from '../utils/apiV2';
import QuickAddBooking from './QuickAddBooking';
import { invalidateFinancials } from '../hooks/useFinancials';
import { BACKEND_URL } from '../config/api';
import { getTodayLocal, addDaysToDateString, formatDateHuman, formatTimeHuman, formatDateTimeHuman } from '../utils/dateUtils';
import { getTimezoneAbbr } from '../utils/timezones';

// Expandable description component for add-ons
const ExpandableAddOnDescription = ({ description, maxLength = 80 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!description) return null;
  
  const shouldTruncate = description.length > maxLength;
  const displayText = shouldTruncate && !isExpanded 
    ? description.substring(0, maxLength).trim() + '...'
    : description;
  
  return (
    <div className="mt-1">
      <p className="text-sm text-gray-600">
        {displayText}
      </p>
      {shouldTruncate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
        >
          {isExpanded ? (
            <>See less <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>See more <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}
    </div>
  );
};

// Currency formatting utility - supports all currencies properly
const formatCurrency = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  } catch (e) {
    // Fallback for invalid currency codes
    return `${currency || '$'} ${(amount || 0).toFixed(2)}`;
  }
};

// Helper to calculate end time from start time + duration
const calculateEndTime = (startTime, durationMinutes) => {
  if (!startTime) return '00:00';
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
};

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const AddBookingWizard = ({ assets, teamMembers, roles, onComplete, onCancel }) => {
  return (
    <Elements stripe={stripePromise}>
      <AddBookingWizardForm 
        assets={assets} 
        teamMembers={teamMembers} 
        roles={roles} 
        onComplete={onComplete} 
        onCancel={onCancel} 
      />
    </Elements>
  );
};

const AddBookingWizardForm = ({ assets, teamMembers, roles, onComplete, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currencySymbol } = useOperatorLocale();
  const minCharge = useMinChargeFloor();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState(null);

  // 🎁 Gift card hold (Phase 3.3) — operator-side application during manual booking
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  
  // NEW: Load services
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loadingServices, setLoadingServices] = useState(false);
  
  // Track if user has started booking flow (clicked a service) - hides tabs
  const [bookingFlowStarted, setBookingFlowStarted] = useState(false);
  
  // Get organization timezone from settings (source of truth)
  // Falls back to America/Chicago (CST) for US orgs
  const orgTimezone = localStorage.getItem('orgTimezone') || 'America/Chicago';
  
  // Get organization's native currency (for consistent pricing display)
  // All prices should be shown in org's currency until multi-currency step
  const orgCurrency = localStorage.getItem('orgCurrency') || 'USD';
  
  // Listen for messages from embedded widget
  useEffect(() => {
    const handleWidgetMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      
      const { type } = event.data || {};
      
      if (type === 'SERVICE_SELECTED' || type === 'OPEN_BOOKING_MODAL') {
        // User clicked a service - hide the tabs
        setBookingFlowStarted(true);
      }
      
      if (type === 'BOOKING_COMPLETE' || type === 'BOOKING_CREATED') {
        // The widget already created the booking — light-touch only here.
        // (Heavy refresh waits for WIDGET_CLOSE: reloading data now re-renders
        // the calendar and remounts the iframe, killing the success panel.)
        invalidateFinancials();
        if (type === 'BOOKING_COMPLETE') {
          const code = event.data?.data?.confirmationCode;
          toast.success(code ? `Booking ${code} confirmed` : 'Booking confirmed');
        }
      }

      if (type === 'WIDGET_CLOSE') {
        // Staff clicked Done / closed the widget flow
        if (window.refreshBookingData) window.refreshBookingData();
        if (onCancel) onCancel();
      }
      
      if (type === 'CLOSE_BOOKING_MODAL' || type === 'MODAL_CLOSED') {
        // User closed the widget modal - show tabs again
        setBookingFlowStarted(false);
      }
    };
    
    window.addEventListener('message', handleWidgetMessage);
    return () => window.removeEventListener('message', handleWidgetMessage);
  }, [onCancel]);
  
  // Use centralized timezone abbreviation utility
  const getTimezoneAbbreviation = (tz) => {
    return getTimezoneAbbr(tz);
  };
  
  const [formData, setFormData] = useState({
    serviceId: '',  // NEW: Required field
    serviceName: '',  // NEW: For display
    date: getTodayLocal(),
    startTime: '09:00',
    endTime: '11:00',
    duration: 120,
    groupSize: 1,
    selectedDurationIndex: null,
    selectedAddOns: {}, // {addonId: {title, price, type, description}}
    chargeAmount: '', // Editable amount for partial payments
    assetId: '',
    teamAssignments: [],
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    pickupLocation: '',
    paymentStatus: '', // No default - user must select
    totalAmount: 0,
    paymentMethod: '',
    timezone: orgTimezone,
    contactInfoConfirmed: false,  // NEW: Confirmation checkbox
    // Stripe card details
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: '',
    // Multi-currency support
    presentmentCurrency: '', // Customer's preferred currency (empty = service default)
  });

  const totalSteps = 6; // Updated: 1=Service, 2=Date/Time/Capacity, 3=Add-ons, 4=Customer, 5=Review, 6=Payment
  
  // Entry point selection - now includes 'custom' for custom bookings
  const [entryPoint, setEntryPoint] = useState('quick'); // 'quick' | 'service' | 'custom', 'date', or 'custom'
  const quickAddRef = useRef(null);
  const [quickSubmit, setQuickSubmit] = useState({ submitting: false, mode: 'external' });
  
  // Custom booking state
  const [customBookingData, setCustomBookingData] = useState({
    serviceName: '',
    serviceDescription: '',
    basePrice: '',
    taxRate: '', // Tax rate percentage (e.g., 8.25 for 8.25%)
    notes: '',
    customAddOns: [], // [{description: string, amount: number}]
    durationModel: 'slot_based', // slot_based | day_pass | flexible_dates — drives the schedule fields
    checkOutDate: '',            // flexible_dates only (YYYY-MM-DD)
  });
  const [allOrgAddOns, setAllOrgAddOns] = useState([]); // Add-ons from all services for custom bookings
  
  // Multi-currency support state
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  const [convertedPrice, setConvertedPrice] = useState(null);
  const [loadingConversion, setLoadingConversion] = useState(false);
  
  // Load services on mount
  useEffect(() => {
    loadServices();
    loadSupportedCurrencies();
  }, []);
  
  // Load supported currencies for multi-currency checkout
  const loadSupportedCurrencies = async () => {
    try {
      // Pass organization country for proper default currency
      const orgCountry = localStorage.getItem('orgCountry') || 'US';
      const response = await api.get(`/admin/supported-currencies?org_country=${orgCountry}`);
      if (response.data?.currencies) {
        // Sort to put guaranteed currencies first
        const sorted = [...response.data.currencies].sort((a, b) => {
          if (a.tier === 'guaranteed' && b.tier !== 'guaranteed') return -1;
          if (a.tier !== 'guaranteed' && b.tier === 'guaranteed') return 1;
          return 0;
        });
        setSupportedCurrencies(sorted);
      }
    } catch (error) {
      console.error('Failed to load supported currencies:', error);
    }
  };
  
  // Convert price when currency changes - supports both service-based and custom bookings
  const convertPrice = async (toCurrency) => {
    const isCustom = entryPoint === 'custom';
    
    // For service-based bookings, require selected service
    // For custom bookings, we just need the custom data
    if (!isCustom && !selectedService) {
      setConvertedPrice(null);
      return;
    }
    
    if (!toCurrency) {
      setConvertedPrice(null);
      return;
    }
    
    // Determine the base currency (org currency for custom, service currency for service-based)
    const baseCurrency = isCustom ? orgCurrency : (selectedService?.currency || 'USD');
    
    if (toCurrency.toLowerCase() === baseCurrency.toLowerCase()) {
      setConvertedPrice(null);
      return;
    }
    
    setLoadingConversion(true);
    try {
      let basePrice, addOnsTotal, preTotal, taxEnabled, taxRateDecimal, salesTax, platformFee, total;
      
      if (isCustom) {
        // Custom booking calculations
        basePrice = parseFloat(customBookingData.basePrice) || 0;
        addOnsTotal = (customBookingData.customAddOns || []).reduce((sum, addon) => sum + (parseFloat(addon.amount) || 0), 0);
        preTotal = basePrice + addOnsTotal;
        
        // Custom tax rate (user-input percentage)
        const customTaxRate = parseFloat(customBookingData.taxRate) || 0;
        taxEnabled = customTaxRate > 0;
        taxRateDecimal = customTaxRate / 100;
        salesTax = taxEnabled ? preTotal * taxRateDecimal : 0;
        
        platformFee = (preTotal * 0.059) + 0.30;
        total = preTotal + platformFee + salesTax;
      } else {
        // Service-based booking calculations
        basePrice = selectedService.basePrice || selectedService.price || 0;
        addOnsTotal = Object.values(formData.selectedAddOns || {}).reduce((sum, addon) => sum + (addon?.price || 0), 0);
        preTotal = basePrice + addOnsTotal;
        platformFee = (preTotal * 0.059) + 0.30;
        // Check both taxEnabled and salesTaxEnabled (different naming conventions)
        taxEnabled = selectedService.taxEnabled || selectedService.salesTaxEnabled || false;
        let taxRateRaw = selectedService.taxRate || selectedService.salesTaxRate || 0;
        // FIXED: taxRate is ALWAYS stored as percentage (e.g., 1 = 1%, 8.25 = 8.25%), ALWAYS divide by 100
        taxRateDecimal = taxRateRaw / 100;
        salesTax = taxEnabled ? preTotal * taxRateDecimal : 0;
        total = preTotal + platformFee + salesTax;
      }
      
      const response = await api.post('/admin/convert-price', {
        amount: total,
        fromCurrency: baseCurrency,
        toCurrency: toCurrency
      });
      
      if (response.data) {
        setConvertedPrice({
          ...response.data,
          basePrice: basePrice,
          basePriceConverted: basePrice * response.data.rateWithFee
        });
      }
    } catch (error) {
      console.error('Failed to convert price:', error);
      setConvertedPrice(null);
    } finally {
      setLoadingConversion(false);
    }
  };
  
  // Trigger conversion when currency changes
  useEffect(() => {
    if (formData.presentmentCurrency && currentStep === 6) {
      convertPrice(formData.presentmentCurrency);
    }
  }, [formData.presentmentCurrency, currentStep, entryPoint, customBookingData.basePrice, customBookingData.taxRate]);
  
  // Load all org add-ons when custom mode is selected
  useEffect(() => {
    if (entryPoint === 'custom' && services.length > 0) {
      // Collect unique add-ons from all services
      const addOnsMap = new Map();
      services.forEach(service => {
        if (service.addOns && Array.isArray(service.addOns)) {
          service.addOns.forEach(addon => {
            const key = addon.title || addon.name;
            if (key && !addOnsMap.has(key)) {
              addOnsMap.set(key, {
                id: addon.id || `addon-${key}`,
                title: addon.title || addon.name,
                description: addon.description || '',
                price: addon.price || 0,
                type: addon.type || 'checkbox',
                fromService: service.name
              });
            }
          });
        }
      });
      setAllOrgAddOns(Array.from(addOnsMap.values()));
    }
  }, [entryPoint, services]);
  
  // Track expanded descriptions
  const [expandedServices, setExpandedServices] = useState({});
  
  // Track if we're intentionally selecting a service (not navigating back)
  const [isSelectingService, setIsSelectingService] = useState(false);
  
  // Track if we just selected a time (for auto-advance)
  const [justSelectedTime, setJustSelectedTime] = useState(false);
  
  // Track sub-view within step 2 (calendar → time slots with inline capacity)
  const [step2SubView, setStep2SubView] = useState('calendar'); // 'calendar', 'timeSlots'
  
  // Real-time availability state
  const [availabilityData, setAvailabilityData] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityReady, setAvailabilityReady] = useState(false);
  
  // Month availability for calendar highlighting (SAME ENGINE as customer widget)
  const [monthAvailability, setMonthAvailability] = useState({});
  const [monthAvailabilityLoading, setMonthAvailabilityLoading] = useState(false);
  
  // Track current calendar month for fetching
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  
  // Fetch month availability when service or month changes (SAME API as customer widget)
  useEffect(() => {
    const fetchMonthAvailability = async () => {
      if (!selectedService?.id) {
        setMonthAvailability({});
        return;
      }
      
      setMonthAvailabilityLoading(true);
      
      try {
        const orgId = localStorage.getItem('organizationId');
        // Use the SAME API as customer widget: /v2/public/services/{id}/availability-month
        const response = await api.post(
          `/v2/public/services/${selectedService.id}/availability-month`,
          null,
          { 
            params: { 
              year: calendarMonth.year, 
              month: calendarMonth.month + 1, // API uses 1-indexed months
              org_id: orgId 
            }
          }
        );
        
        setMonthAvailability(response.data?.days || {});
      } catch (error) {
        setMonthAvailability({});
      } finally {
        setMonthAvailabilityLoading(false);
      }
    };
    
    if (selectedService?.id && currentStep === 2) {
      fetchMonthAvailability();
    }
  }, [selectedService?.id, calendarMonth.year, calendarMonth.month, currentStep]);
  
  // Fetch real-time availability when date changes - PRE-FETCH before showing time slots
  // OPTIMIZATION: First check if slots are already in monthAvailability data
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedService?.id || !formData.date) {
        setAvailabilityData(null);
        setAvailabilityReady(false);
        return;
      }
      
      // OPTIMIZATION: Check if we already have slots from month availability
      const cachedDayData = monthAvailability[formData.date];
      if (cachedDayData?.slots && cachedDayData.slots.length > 0) {
        // Normalize slot data to include durations array for compatibility
        const normalizedSlots = cachedDayData.slots.map(slot => ({
          ...slot,
          // Add durations array if not present (for month API compatibility)
          durations: slot.durations || [{
            hours: (selectedService?.durationMinutes || selectedService?.duration || 60) / 60,
            price: selectedService?.basePrice || selectedService?.price || 0,
            available: slot.availableCapacity ?? slot.remaining_capacity ?? 10
          }]
        }));
        setAvailabilityData({ slots: normalizedSlots });
        setAvailabilityLoading(false);
        setAvailabilityReady(true);
        return;
      }
      
      // Start loading immediately - need to fetch
      setAvailabilityLoading(true);
      setAvailabilityReady(false);
      
      try {
        const orgId = localStorage.getItem('organizationId');
        const formDataPayload = new FormData();
        formDataPayload.append('date', formData.date);
        formDataPayload.append('org_id', orgId);
        
        const response = await api.post(
          `/public/services/${selectedService.id}/availability-slots`,
          formDataPayload,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setAvailabilityData(response.data);
      } catch (error) {
        console.error('[AddBookingWizard] Failed to fetch availability:', error);
        setAvailabilityData(null);
      } finally {
        setAvailabilityLoading(false);
        setAvailabilityReady(true);
      }
    };
    
    // Fetch immediately when date is selected (not just when viewing time slots)
    if (formData.date && selectedService?.id) {
      fetchAvailability();
    }
  }, [selectedService?.id, formData.date, monthAvailability]);
  
  // Auto-advance to step 2 when service is selected in step 1
  useEffect(() => {
    if (currentStep === 1 && formData.serviceId && entryPoint === 'service' && isSelectingService) {
      // Small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        setCurrentStep(2);
        setStep2SubView('calendar'); // Reset to calendar view
        setIsSelectingService(false); // Reset flag
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formData.serviceId, currentStep, entryPoint, isSelectingService]);
  
  // Calculate dynamic progress within Step 2
  const getStep2Progress = () => {
    if (currentStep !== 2) return currentStep;
    // Step 2 has 2 sub-views, advance progress as user makes selections
    if (step2SubView === 'calendar') return 2.0;
    if (step2SubView === 'timeSlots' && !formData.startTime) return 2.25;
    if (step2SubView === 'timeSlots' && formData.startTime && (!formData.groupSize || formData.groupSize < 1)) return 2.5;
    if (step2SubView === 'timeSlots' && formData.startTime && formData.groupSize >= 1) return 2.75;
    return 2;
  };
  
  const toggleServiceDescription = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };
  
  const loadServices = async () => {
    try {
      setLoadingServices(true);
      // Use v2 API for services (PostgreSQL)
      const response = await orgApi.getServices({ activeOnly: true });
      // v2 may return services array or nested in .services
      setServices(response.data?.services || response.data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoadingServices(false);
    }
  };

  // Fetch full service details including addons and custom questions
  const fetchFullServiceDetails = async (serviceId) => {
    try {
      const orgId = localStorage.getItem('organizationId');
      const response = await orgApi.getService(serviceId);
      const fullService = response.data;
      return fullService;
    } catch (error) {
      return null;
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      // For custom bookings, skip step 2 (date/time/capacity) - it's already in step 1
      if (entryPoint === 'custom' && currentStep === 1) {
        setCurrentStep(3); // Skip to Add-ons
      } else {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
    }
  };

  const prevStep = () => {
    // For custom bookings, skip step 2 when going back
    if (entryPoint === 'custom' && currentStep === 3) {
      setCurrentStep(1); // Go back to custom form
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        // Step 1: Service selection or Custom booking details
        if (entryPoint === 'custom') {
          // Custom booking validation
          if (!customBookingData.serviceName?.trim()) {
            toast.error('Please enter a service/item description');
            return false;
          }
          if (!customBookingData.basePrice || parseFloat(customBookingData.basePrice) <= 0) {
            toast.error('Please enter a valid price');
            return false;
          }
          if (!formData.date) {
            toast.error('Please select a date');
            return false;
          }
          if (!formData.startTime) {
            toast.error('Please select a time');
            return false;
          }
          return true;
        } else {
          // Regular service selection validation
          if (!formData.serviceId) {
            toast.error('Please select a service');
            return false;
          }
          return true;
        }
      case 2:
        // Step 2: Date, time, and capacity (skip for custom - already done in step 1)
        if (entryPoint === 'custom') {
          return true; // Custom bookings handle this in step 1
        }
        if (!formData.date || !formData.startTime || !formData.endTime) {
          toast.error('Please select date and time');
          return false;
        }
        if (!formData.groupSize || formData.groupSize < 1) {
          toast.error('Please select number of guests');
          return false;
        }
        // Validate party size against REAL-TIME slot capacity (not just service max)
        if (availabilityData?.slots) {
          const selectedSlot = availabilityData.slots.find(s => (s.time || s) === formData.startTime);
          const slotCapacity = selectedSlot?.availableCapacity ?? selectedSlot?.durations?.[0]?.available;
          if (slotCapacity !== undefined && formData.groupSize > slotCapacity) {
            toast.error(`Only ${slotCapacity} spots remaining for this time slot. Please reduce party size or choose a different time.`);
            return false;
          }
        }
        // Also validate against service max capacity
        if (selectedService?.capacity && formData.groupSize > selectedService.capacity) {
          toast.error(`Maximum capacity is ${selectedService.capacity} guests. Please reduce group size or book multiple trips.`);
          return false;
        }
        return true;
      case 3:
        // Step 3: Add-ons (optional, always valid)
        return true;
      case 4:
        // Step 4: Customer info
        if (!formData.customerName || !formData.customerName.trim()) {
          toast.error('Customer name is required');
          return false;
        }
        if (!formData.customerEmail || !formData.customerEmail.trim()) {
          toast.error('Customer email is required');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.customerEmail)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        if (!formData.customerPhone || !formData.customerPhone.trim()) {
          toast.error('Customer phone is required');
          return false;
        }
        return true;
      case 5:
        // Step 5: Review - no validation needed, payment selection optional
        return true;
      case 6:
        // Step 6: Payment - must select payment status
        if (!formData.paymentStatus) {
          toast.error('Please select a payment option');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Validate required fields
      if (!formData.startTime) {
        toast.error('Missing time information');
        setIsSubmitting(false);
        return;
      }
      
      // For custom bookings, validate custom fields
      const isCustomBooking = entryPoint === 'custom';
      if (isCustomBooking) {
        if (!customBookingData.serviceName?.trim()) {
          toast.error('Please enter a service/item description');
          setIsSubmitting(false);
          return;
        }
        if (!customBookingData.basePrice || parseFloat(customBookingData.basePrice) <= 0) {
          toast.error('Please enter a valid price');
          setIsSubmitting(false);
          return;
        }
        if (customBookingData.durationModel === 'flexible_dates' && !customBookingData.checkOutDate) {
          toast.error('Please choose a check-out date for this multi-day booking');
          setIsSubmitting(false);
          return;
        }
        if (customBookingData.durationModel === 'flexible_dates'
            && customBookingData.checkOutDate && formData.date
            && customBookingData.checkOutDate <= formData.date) {
          toast.error('Check-out date must be after check-in');
          setIsSubmitting(false);
          return;
        }
      }
      
      // Handle payment based on selected option - include full add-on details (title, price, etc.)
      const addOnsArray = Object.values(formData.selectedAddOns).map(addon => ({
        id: addon.id,
        title: addon.title,
        description: addon.description || '',
        price: addon.price,
        type: addon.type || 'checkbox'
      }));
      
      // For custom bookings, also include custom add-ons
      const customAddOnsArray = isCustomBooking 
        ? customBookingData.customAddOns.map((addon, idx) => ({
            id: `custom_addon_${idx}`,
            title: addon.description,
            description: addon.description,
            price: addon.amount,
            type: 'custom'
          }))
        : [];
      
      const allAddOns = [...addOnsArray, ...customAddOnsArray];
      const addOnsTotal = allAddOns.reduce((sum, addon) => sum + (addon.price || 0), 0);

      // Build booking data - handle custom vs regular
      const bookingData = isCustomBooking ? {
        // Custom booking fields
        isCustomBooking: true,
        bookingSource: 'internal-custom',
        serviceId: null,
        serviceName: customBookingData.serviceName,
        servicePrice: parseFloat(customBookingData.basePrice) || 0,
        durationModel: customBookingData.durationModel || 'slot_based',
        checkOutDate: customBookingData.durationModel === 'flexible_dates' ? (customBookingData.checkOutDate || undefined) : undefined,
        preferredDate: formData.date,
        preferredTime: formData.startTime,
        duration: formData.duration || 60,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        partySize: formData.groupSize,
        pickupLocation: formData.pickupLocation || '',
        notes: customBookingData.notes || '',
        selectedAddOns: allAddOns,
        customAddOns: customBookingData.customAddOns,
        addOnsTotal: addOnsTotal,
        paymentStatus: formData.paymentStatus,
        isInternalBooking: true,
        currency: 'USD'
      } : {
        // Regular booking fields
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        servicePrice: selectedService?.price || 0,
        preferredDate: formData.date,
        preferredTime: formData.startTime,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        partySize: formData.groupSize,
        pickupLocation: formData.pickupLocation || '',
        selectedAddOns: addOnsArray,
        addOnsTotal: addOnsTotal,
        paymentStatus: formData.paymentStatus,
        isInternalBooking: true,
        presentmentCurrency: formData.presentmentCurrency || ''
      };

      // For "Pay Now" or "Share Payment Link", create Stripe Checkout session
      if (formData.paymentStatus === 'pay_now_redirect' || formData.paymentStatus === 'send_link' || formData.paymentStatus === 'deposit') {
        // Calculate pricing for modal - USE CORRECT FIELD NAMES
        const base_price = isCustomBooking 
          ? parseFloat(customBookingData.basePrice) || 0 
          : parseFloat(selectedService?.basePrice || selectedService?.price || 0);
        const add_ons_total = addOnsTotal;
        const pre_total = base_price * formData.groupSize + add_ons_total;
        const platform_fee = (pre_total * 0.059) + 0.30;
        const tax_enabled = isCustomBooking ? (parseFloat(customBookingData.taxRate) > 0) : (selectedService?.taxEnabled || selectedService?.salesTaxEnabled || false);
        let tax_rate_raw = isCustomBooking ? (parseFloat(customBookingData.taxRate) || 0) : (selectedService?.taxRate || selectedService?.salesTaxRate || 0);
        // FIXED: taxRate is ALWAYS stored as percentage (e.g., 1 = 1%, 8.25 = 8.25%), ALWAYS divide by 100
        const tax_rate_decimal = tax_rate_raw / 100;
        const sales_tax = tax_enabled ? pre_total * tax_rate_decimal : 0;
        const total_price = pre_total + platform_fee + sales_tax;
        
        // Get currency from service or presentment currency selection
        const currency = formData.presentmentCurrency || selectedService?.currency || 'USD';
        
        // Add pricing to booking data for v2 API
        bookingData.basePrice = base_price;
        bookingData.partySize = formData.groupSize;
        bookingData.addOnsTotal = add_ons_total;
        bookingData.platformFee = platform_fee;
        bookingData.taxEnabled = tax_enabled;
        bookingData.taxRate = tax_rate_raw;
        bookingData.salesTax = sales_tax;
        bookingData.totalPrice = total_price;
        bookingData.endTime = formData.endTime;
        bookingData.duration = formData.duration || selectedService?.durationMinutes || selectedService?.duration || 60;
        bookingData.currency = currency;  // Include currency for multi-currency support
        bookingData.presentmentCurrency = formData.presentmentCurrency;

        // 🎁 Gift card hold (Phase 3.3) — passes the hold_id through to the
        // backend, which validates and reduces the Stripe charge.
        if (appliedGiftCard?.holdId) {
          bookingData.giftCardHoldId = appliedGiftCard.holdId;
          bookingData.giftCardAmount = appliedGiftCard.applied;
        }
        
        // Deposit flag
        if (formData.paymentStatus === 'deposit') {
          bookingData.payDeposit = true;
          bookingData.paymentStatus = 'deposit';
        }
        
        // Use v2 API for PostgreSQL
        const response = await orgApi.post('/internal-booking/create-with-checkout', bookingData);
        
        if (response.data.success) {
          const checkoutUrl = response.data.checkoutUrl;
          
          if (formData.paymentStatus === 'pay_now_redirect' || formData.paymentStatus === 'deposit') {
            // Redirect admin to Stripe to enter card
            toast.success(formData.paymentStatus === 'deposit' ? 'Opening deposit payment page...' : 'Opening Stripe payment page...');
            // Open in same window for PWA/app compatibility
            window.location.href = checkoutUrl;
            return; // Don't call onComplete since we're navigating away
          } else if (formData.paymentStatus === 'send_link') {
            // Show share link modal with currency
            setPaymentLinkData({
              checkoutUrl: checkoutUrl,
              customerEmail: formData.customerEmail,
              customerPhone: formData.customerPhone,
              bookingDetails: {
                serviceName: formData.serviceName,
                preferredDate: formData.date,
                preferredTime: formData.startTime,
                customerName: formData.customerName,
                totalAmount: total_price,
                currency: response.data.currency || currency  // Use response currency or fallback
              }
            });
            setShowShareLinkModal(true);
          }
          return;
        } else {
          throw new Error(response.data.message || 'Failed to create payment link');
        }
      }
      
      // For "Already Paid", "Will Pay Later" or "Tap to Pay" - use v2 PostgreSQL API
      if (formData.paymentStatus === 'paid' || formData.paymentStatus === 'unpaid' || formData.paymentStatus === 'tap_to_pay') {
        // Ensure required fields are present and valid
        const customerName = (bookingData.customerName || '').trim();
        const customerEmail = (bookingData.customerEmail || '').trim();
        
        if (!customerName || customerName.length < 2) {
          toast.error('Customer name is required (minimum 2 characters)');
          setIsSubmitting(false);
          return;
        }
        
        if (!customerEmail) {
          toast.error('Customer email is required');
          setIsSubmitting(false);
          return;
        }
        
        // Transform booking data to v2 format - use undefined instead of null for optional fields
        const v2BookingData = {
          booking_date: bookingData.preferredDate || bookingData.date,
          start_time: bookingData.preferredTime || bookingData.startTime,
          end_time: bookingData.endTime || calculateEndTime(bookingData.preferredTime || bookingData.startTime, bookingData.duration || 60),
          customer_name: customerName,
          customer_email: customerEmail,
          party_size: bookingData.partySize || 1,
          base_price: parseFloat(bookingData.basePrice || bookingData.servicePrice || 0),
          total_price: parseFloat(bookingData.totalPrice || bookingData.servicePrice || 0),
          payment_status: formData.paymentStatus === 'paid' ? 'paid' : 'pending',
          amount_paid: formData.paymentStatus === 'paid' ? parseFloat(bookingData.totalPrice || bookingData.servicePrice || 0) : 0,
          source: 'admin',  // Mark as internal/admin booking for cron job protection
        };
        
        // Only add optional fields if they have values
        if (bookingData.serviceId) v2BookingData.service_id = bookingData.serviceId;
        if (bookingData.serviceName) v2BookingData.service_name = bookingData.serviceName;
        if (bookingData.isCustomBooking) v2BookingData.is_custom_booking = true;
        if (bookingData.durationModel) v2BookingData.duration_model = bookingData.durationModel;
        if (bookingData.checkOutDate) v2BookingData.check_out_date = bookingData.checkOutDate;
        if (bookingData.assetId) v2BookingData.asset_id = bookingData.assetId;
        if (bookingData.teamMemberId) v2BookingData.team_member_id = bookingData.teamMemberId;
        if (bookingData.customerPhone) v2BookingData.customer_phone = bookingData.customerPhone;
        if (bookingData.notes) v2BookingData.internal_notes = bookingData.notes;
        
        const response = await orgApi.createBooking(v2BookingData);
        
        if (response.data?.success || response.data?.booking) {
          toast.success('Booking created successfully!');
          
          // Notify setup guide of data change
          window.dispatchEvent(new Event('setupDataChanged'));
          
          // Extract booking data from response (could be in response.data.booking or response.data directly)
          const bookingResponse = response.data.booking || response.data;

          // 📲 Tap to Pay — collect the full amount in person right now.
          // The booking exists either way; a failed/canceled tap leaves it unpaid.
          if (formData.paymentStatus === 'tap_to_pay' && bookingResponse.id) {
            const tId = toast.loading('Preparing reader…');
            try {
              await collectInPerson({
                orgApi,
                booking: { id: bookingResponse.id },
                onStatus: (m) => toast.loading(m, { id: tId }),
              });
              toast.success('Paid in full — card tapped ✅', { id: tId });
            } catch (tapErr) {
              if (tapErr?.code === 'canceled') {
                toast.message('Tap canceled — booking saved unpaid', { id: tId, description: 'Collect anytime from the manifest.' });
              } else {
                const d = tapErr?.response?.data?.detail;
                toast.error(typeof d === 'string' ? d : (tapErr.message || 'Tap to Pay could not complete'), { id: tId, description: 'Booking saved unpaid — collect from the manifest.' });
              }
            }
          }

          setCreatedBooking({
            ...bookingData,
            ...bookingResponse,
            confirmationCode: bookingResponse.confirmationCode || bookingResponse.confirmation_code
          });
          setShowSuccessModal(true);
          invalidateFinancials();
          return;
        } else {
          throw new Error(response.data?.message || 'Booking creation failed');
        }
      }
      
      toast.error('Please select a payment option');
      setIsSubmitting(false);
    } catch (error) {
      console.error('=== ERROR IN HANDLE SUBMIT ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle Pydantic validation errors (array of objects with type, loc, msg)
      let errorMessage = 'Failed to create booking';
      const detail = error.response?.data?.detail;
      
      if (Array.isArray(detail)) {
        // Pydantic validation error - extract messages
        errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Widget's own checkout modal is open inside the iframe → elevate to full viewport
  const widgetExpanded = entryPoint === 'service' && currentStep === 1 && bookingFlowStarted;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // NEW: Service Selection Step with THREE entry points
        return (
          <div className={widgetExpanded ? 'h-full' : 'space-y-4'}>
            {/* Entry Point Selector — shared ModalTabSelector (same control as Roles/Team modals) */}
            {/* Hide tabs once user clicks a service and starts booking flow */}
            {!bookingFlowStarted && (
              <ModalTabSelector
                className="mb-4"
                value={entryPoint}
                onChange={setEntryPoint}
                tabs={[
                  { value: 'quick', label: 'Quick Add', icon: Zap },
                  { value: 'service', label: 'Service', icon: Package },
                ]}
              />
            )}

            {/* Quick Add — lightweight manual/external booking logger */}
            {entryPoint === 'quick' && (
              <QuickAddBooking
                ref={quickAddRef}
                onSubmittingChange={(submitting, mode) => setQuickSubmit({ submitting, mode })}
                services={services}
                onCreated={(booking) => {
                  toast.success('Booking added!');
                  window.dispatchEvent(new Event('setupDataChanged'));
                  setCreatedBooking(booking);
                  setShowSuccessModal(true);
                  invalidateFinancials();
                }}
              />
            )}
            
            {/* Custom Booking Flow */}
            {entryPoint === 'custom' && (
              <div className="space-y-4">
                {/* Custom Service Name */}
                <div>
                  <Label>Service/Item Description <span className="text-red-500">*</span></Label>
                  <Input
                    value={customBookingData.serviceName}
                    onChange={(e) => setCustomBookingData({...customBookingData, serviceName: e.target.value})}
                    placeholder="e.g., BBQ Tray + Sparkling Water, 10 Boat Charter..."
                    className="mt-1"
                  />
                </div>
                
                {/* Base Price */}
                <div>
                  <Label>Price <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{currencySymbol}</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={customBookingData.basePrice}
                      onChange={(e) => setCustomBookingData({...customBookingData, basePrice: e.target.value})}
                      placeholder="0.00"
                      className={currencySymbol.length > 1 ? 'pl-14' : 'pl-7'}
                      data-testid="custom-booking-base-price"
                    />
                  </div>
                  {minCharge.isBelow(customBookingData.basePrice) && (
                    <p className="mt-1 text-xs text-amber-600">
                      Price must be at least {minCharge.formatted} for Stripe-processed bookings.
                    </p>
                  )}
                </div>
                
                {/* Tax Rate */}
                <div>
                  <Label>Tax Rate (%)</Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={customBookingData.taxRate}
                      onChange={(e) => setCustomBookingData({...customBookingData, taxRate: e.target.value})}
                      placeholder="0.00"
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no tax</p>
                </div>

                {/* Booking type — picks the duration model (drives the schedule fields below) */}
                <div>
                  <Label className="mb-1.5 block">Booking type</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'slot_based', label: 'Timed', sub: 'Start + duration', icon: Clock },
                      { value: 'day_pass', label: 'Day Pass', sub: 'All-day access', icon: Sun },
                      { value: 'flexible_dates', label: 'Multi-Day', sub: 'Check-in/out', icon: CalendarDays },
                    ].map((opt) => {
                      const OptIcon = opt.icon;
                      const active = customBookingData.durationModel === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`custom-bookingtype-${opt.value}`}
                          onClick={() => setCustomBookingData({ ...customBookingData, durationModel: opt.value })}
                          className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-all ${
                            active
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-gray-200 bg-white hover:border-blue-200'
                          }`}
                        >
                          <OptIcon className={`w-4 h-4 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                          <span className={`text-xs font-semibold ${active ? 'text-blue-700' : 'text-gray-700'}`}>{opt.label}</span>
                          <span className="text-[10px] text-gray-400 leading-tight">{opt.sub}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Schedule — adapts to the chosen booking type */}
                {customBookingData.durationModel === 'day_pass' ? (
                  /* Day pass: date only */
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date <span className="text-red-500">*</span></Label>
                      <Input type="date" value={formData.date}
                        onChange={(e) => updateFormData('date', e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label>Access</Label>
                      <div className="mt-1 h-10 flex items-center gap-2 px-3 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-700 font-medium">
                        <Sun className="w-4 h-4" /> All-day
                      </div>
                    </div>
                  </div>
                ) : customBookingData.durationModel === 'flexible_dates' ? (
                  /* Multi-day: check-in date + check-out date + check-in time */
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Check-in <span className="text-red-500">*</span></Label>
                        <Input type="date" value={formData.date}
                          onChange={(e) => updateFormData('date', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Check-out <span className="text-red-500">*</span></Label>
                        <Input type="date" value={customBookingData.checkOutDate} min={formData.date}
                          onChange={(e) => setCustomBookingData({ ...customBookingData, checkOutDate: e.target.value })} className="mt-1" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Check-in time</Label>
                        <Input type="time" value={formData.startTime}
                          onChange={(e) => updateFormData('startTime', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Party Size</Label>
                        <Input type="number" min="1" value={formData.groupSize}
                          onChange={(e) => updateFormData('groupSize', parseInt(e.target.value) || 1)} className="mt-1" />
                      </div>
                    </div>
                    {customBookingData.checkOutDate && formData.date && (
                      <p className="text-xs text-gray-500">
                        {Math.max(0, Math.round((new Date(customBookingData.checkOutDate + 'T12:00:00') - new Date(formData.date + 'T12:00:00')) / 86400000))} night stay
                      </p>
                    )}
                  </>
                ) : (
                  /* Timed (slot_based): date + time + duration */
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Date <span className="text-red-500">*</span></Label>
                        <Input type="date" value={formData.date}
                          onChange={(e) => updateFormData('date', e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Time <span className="text-red-500">*</span></Label>
                        <Input type="time" value={formData.startTime}
                          onChange={(e) => updateFormData('startTime', e.target.value)} className="mt-1" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Duration</Label>
                        <Select value={String(formData.duration || 60)} onValueChange={(val) => updateFormData('duration', parseInt(val))}>
                          <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[[15,'15 min'],[30,'30 min'],[45,'45 min'],[60,'1 hour'],[90,'1.5 hours'],[120,'2 hours'],[180,'3 hours'],[240,'4 hours'],[300,'5 hours'],[360,'6 hours'],[480,'8 hours'],[720,'12 hours'],[1440,'24 hours']].map(([v,l]) => (
                              <SelectItem key={v} value={String(v)}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Party Size</Label>
                        <Input type="number" min="1" value={formData.groupSize}
                          onChange={(e) => updateFormData('groupSize', parseInt(e.target.value) || 1)} className="mt-1" />
                      </div>
                    </div>
                  </>
                )}
                
                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={customBookingData.notes}
                    onChange={(e) => setCustomBookingData({...customBookingData, notes: e.target.value})}
                    placeholder="Additional details about this booking..."
                    className="mt-1 resize-none"
                    rows={3}
                  />
                </div>
              </div>
            )}
            
            {/* Service First Flow - Embed the Customer Widget */}
            {entryPoint === 'service' && (() => {
              const orgId = localStorage.getItem('organizationId');
              const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
              
              if (!orgId) {
                return (
                  <div className="flex flex-col items-center justify-center h-[70vh] text-center px-4">
                    <Package className="w-12 h-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 font-medium">Organization not found</p>
                    <p className="text-sm text-gray-500 mt-1">Please log out and log back in</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      Refresh Page
                    </button>
                  </div>
                );
              }
              
              const widgetUrl = `/services-grid.html?org_id=${orgId}&embed=true&internal=true&flow=internal-booking&api=${encodeURIComponent(backendUrl)}`;
              
              return (
                <div className={widgetExpanded ? 'h-full' : 'h-[75vh] -mx-2 -mb-6 mt-0 px-2'}>
                  <iframe
                    key={`widget-${orgId}`}
                    src={widgetUrl}
                    className={`w-full h-full border-0 ${widgetExpanded ? '' : 'rounded-lg'}`}
                    title="Booking Widget"
                    allow="payment"
                    style={widgetExpanded ? undefined : { minHeight: '600px' }}
                  />
                </div>
              );
            })()}
          </div>
        );
      
      case 2:
        // Date & Time - Multi-view flow like customer widget
        return (
          <div className="space-y-4">
            {/* Top navigation with back arrow */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  if (step2SubView === 'timeSlots') {
                    setStep2SubView('calendar');
                  } else {
                    setCurrentStep(1); // Back to services
                  }
                }}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                {step2SubView === 'calendar' && 'Back to services'}
                {step2SubView === 'timeSlots' && 'Back to calendar'}
              </button>
              
              {/* Service name + booking type indicator */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                  (selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset')
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {(selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset') ? (
                    <>
                      <Package className="w-3 h-3" />
                      <span>Per-Asset</span>
                    </>
                  ) : (
                    <>
                      <Users className="w-3 h-3" />
                      <span>Per-Ticket</span>
                    </>
                  )}
                </div>
                <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200">
                  {selectedService?.name}
                </div>
              </div>
            </div>

            {/* Sub-View: Calendar */}
            {step2SubView === 'calendar' && (
              <ServiceCalendarPicker
                service={selectedService}
                selectedDate={formData.date}
                selectedTime={formData.startTime}
                onDateSelect={(date) => {
                  updateFormData('date', date);
                  updateFormData('startTime', '');
                  updateFormData('endTime', '');
                  updateFormData('groupSize', 1);
                  // Advance to time slots view
                  setStep2SubView('timeSlots');
                }}
                onTimeSelect={() => {}} // Not used in calendar view
                hideTimeSlots={true}
                // Pass API-based availability (SAME ENGINE as customer widget!)
                monthAvailability={monthAvailability}
                monthAvailabilityLoading={monthAvailabilityLoading}
                onMonthChange={(newMonth) => setCalendarMonth(newMonth)}
              />
            )}

            {/* Sub-View: Time Slots with inline duration/capacity */}
            {step2SubView === 'timeSlots' && formData.date && (
              <div className="space-y-4">
                {/* Date header with navigation */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = addDaysToDateString(formData.date, -1);
                      updateFormData('date', newDate);
                      updateFormData('startTime', '');
                      // Don't reset groupSize - preserve user's selection
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-semibold">
                    {formatDateHuman(formData.date, { includeDay: true })}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = addDaysToDateString(formData.date, 1);
                      updateFormData('date', newDate);
                      updateFormData('startTime', '');
                      // Don't reset groupSize - preserve user's selection
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Real-Time Availability Status Indicator - Smooth transitions */}
                <div className="flex items-center justify-center gap-2 py-2 mb-3 min-h-[28px] transition-all duration-300">
                  {availabilityLoading ? (
                    <div className="flex items-center gap-2 text-blue-600 animate-pulse">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                      <span className="text-xs font-medium">Checking real-time availability...</span>
                    </div>
                  ) : availabilityReady && availabilityData ? (
                    <div className="flex items-center gap-1.5 text-green-600 animate-in fade-in duration-300">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs font-medium">
                        Availability checked • {availabilityData.slots?.length || 0} time slots
                      </span>
                    </div>
                  ) : availabilityReady && !availabilityData ? (
                    <div className="flex items-center gap-1.5 text-amber-600 animate-in fade-in duration-300">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-xs font-medium">Could not check availability</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" strokeWidth={2} />
                      </svg>
                      <span className="text-xs font-medium">Waiting for availability data...</span>
                    </div>
                  )}
                </div>

                {/* Available times - Show loading skeleton until ready */}
                {!availabilityReady ? (
                  // Smooth loading skeleton
                  <div className="space-y-3 animate-pulse">
                    <div className="grid grid-cols-2 gap-3">
                      {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="h-14 rounded-lg bg-gray-200"></div>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Actual time slots with smooth fade-in
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                {(() => {
                  const formatTime = (time24) => {
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    return `${hour12}:${minutes} ${ampm}`;
                  };

                  const isPerAsset = selectedService?.bookingMode === 'exclusive' || selectedService?.capacityMode === 'per_asset';
                  
                  // Normalize duration options - handle both {hours: 1} and {duration: 60}
                  let durationOptions = (selectedService?.durationOptions || []).map(opt => ({
                    hours: opt.hours || (opt.duration ? opt.duration / 60 : 1),
                    price: opt.price || selectedService?.basePrice || selectedService?.price || 0
                  }));
                  
                  const hasDurationOptions = durationOptions.length > 0;
                  
                  // Use real-time availability data if available
                  const slots = availabilityData?.slots || [];
                  
                  // Fallback to service config if no real-time data
                  let availableTimes = [];
                  if (slots.length === 0 && !availabilityLoading) {
                    if (selectedService?.availableTimeSlots) {
                      const date = new Date(formData.date + 'T00:00:00');
                      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                      const dayName = dayNames[date.getDay()];
                      
                      selectedService.availableTimeSlots.forEach(slot => {
                        if (slot.days && slot.days.map(d => d.toLowerCase()).includes(dayName)) {
                          availableTimes.push({ time: slot.time, available: true, durations: [] });
                        }
                      });
                      availableTimes.sort((a, b) => a.time.localeCompare(b.time));
                    }
                  } else {
                    availableTimes = slots;
                  }

                  return availableTimes.length > 0 ? (
                    <div className="space-y-3">
                      {/* Time slot buttons with real-time availability */}
                      <div className="grid grid-cols-2 gap-3">
                        {availableTimes.map(slotData => {
                          const time = slotData.time || slotData;
                          const isGreyedOut = slotData.greyedOut;
                          const reason = slotData.reason;
                          const slotDurations = slotData.durations || [];
                          
                          // Check if ANY duration has availability
                          const hasAnyAvailability = slotDurations.length === 0 || slotDurations.some(d => d.available > 0);
                          const isSoldOut = slotDurations.length > 0 && slotDurations.every(d => d.available === 0);
                          
                          return (
                            <button
                              key={time}
                              type="button"
                              disabled={isGreyedOut || isSoldOut}
                              onClick={() => {
                                if (isGreyedOut || isSoldOut) return;
                                
                                if (formData.startTime === time) {
                                  // Deselect if clicking same time - reset everything
                                  updateFormData('startTime', '');
                                  updateFormData('endTime', '');
                                  updateFormData('groupSize', 1);
                                } else {
                                  updateFormData('startTime', time);
                                  // Auto-calculate end time
                                  const serviceDuration = selectedService?.durationMinutes || selectedService?.duration || 60;
                                  const [hours, minutes] = time.split(':').map(Number);
                                  const totalMinutes = hours * 60 + minutes + serviceDuration;
                                  const endHours = Math.floor(totalMinutes / 60) % 24;
                                  const endMinutes = totalMinutes % 60;
                                  const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
                                  updateFormData('endTime', endTime);
                                }
                              }}
                              className={`
                                py-4 rounded-lg text-sm font-semibold transition-all relative
                                ${isGreyedOut || isSoldOut
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : formData.startTime === time 
                                    ? 'bg-blue-800 text-white' 
                                    : 'bg-blue-600 text-white hover:bg-blue-700'}
                              `}
                            >
                              <span>{slotData.displayTime || formatTime(time)} {!slotData.displayTime && getTimezoneAbbreviation(selectedService?.timezone || orgTimezone)}</span>
                              {isSoldOut && (
                                <span className="absolute top-1 right-1 text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded">
                                  SOLD OUT
                                </span>
                              )}
                              {reason === 'minimum_notice' && (
                                <span className="absolute top-1 right-1 text-[10px] bg-gray-500 text-white px-1.5 py-0.5 rounded">
                                  TOO LATE
                                </span>
                              )}
                              {/* Show available count for per-asset */}
                              {hasAnyAvailability && slotDurations.length > 0 && !isGreyedOut && !isSoldOut && (
                                <span className="block text-[10px] mt-1 opacity-80">
                                  {isPerAsset 
                                    ? `${slotDurations[0]?.available || 0} ${slotDurations[0]?.available === 1 ? 'boat' : 'boats'} left`
                                    : `${slotDurations[0]?.available || 0} spots left`
                                  }
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Duration/Capacity appears below selected time */}
                      {formData.startTime && (
                        <div className="pt-3 animate-in slide-in-from-top-2 duration-300">
                          {(() => {
                            // Get real-time availability for selected slot
                            const selectedSlot = availableTimes.find(s => (s.time || s) === formData.startTime);
                            const slotDurations = selectedSlot?.durations || [];
                            
                            // Use real-time data if available, otherwise calculate from service
                            const linkedAssetIds = selectedService?.linkedAssets || selectedService?.assetIds || [];
                            const numAssets = linkedAssetIds.length || 1;
                            let assetCapacity = selectedService?.capacity || 12;
                            
                            if (isPerAsset && linkedAssetIds.length > 0 && assets && assets.length > 0) {
                              const linkedAssetDetails = assets.filter(a => linkedAssetIds.includes(a.id));
                              if (linkedAssetDetails.length > 0) {
                                assetCapacity = Math.min(...linkedAssetDetails.map(a => a.capacity || 12));
                              }
                            }
                            
                            return isPerAsset ? (
                            // Per-Asset: Show booking options with REAL-TIME availability
                            <div className="space-y-2">
                              {hasDurationOptions ? (
                                durationOptions.map((duration, idx) => {
                                  // Get real-time availability for this duration
                                  const realTimeData = slotDurations.find(d => Math.abs(d.hours - duration.hours) < 0.1);
                                  const availableCount = realTimeData?.available ?? numAssets;
                                  const selectedCount = formData.selectedDurationIndex === idx ? (formData.groupSize || 0) : 0;
                                  const remaining = availableCount - selectedCount;
                                  const isSoldOut = availableCount === 0;
                                  
                                  return (
                                    <div
                                      key={idx}
                                      className={`
                                        p-3 border rounded-lg transition-all
                                        ${isSoldOut ? 'border-gray-200 bg-gray-50 opacity-60' : 
                                          formData.selectedDurationIndex === idx ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
                                      `}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className={`font-semibold ${isSoldOut ? 'text-gray-400' : 'text-gray-900'}`}>
                                            {duration.hours} {duration.hours === 1 ? 'Hour' : 'Hours'} - {formatCurrency(realTimeData?.price || duration.price, selectedService.currency)}
                                          </p>
                                          <p className={`text-xs ${isSoldOut ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                            {isSoldOut ? '❌ SOLD OUT' : `${remaining} available (up to ${assetCapacity} guests per booking)`}
                                          </p>
                                        </div>
                                        {!isSoldOut && (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (formData.selectedDurationIndex !== idx) {
                                                  updateFormData('selectedDurationIndex', idx);
                                                  updateFormData('duration', duration.hours * 60);
                                                  updateFormData('groupSize', 1);
                                                } else {
                                                  updateFormData('groupSize', Math.max(1, (formData.groupSize || 0) - 1));
                                                }
                                              }}
                                              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                            >
                                              −
                                            </button>
                                            <span className="w-10 text-center font-bold text-lg">{selectedCount}</span>
                                            <button
                                              type="button"
                                              disabled={selectedCount >= availableCount}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                updateFormData('selectedDurationIndex', idx);
                                                updateFormData('duration', duration.hours * 60);
                                                updateFormData('groupSize', Math.min(availableCount, (selectedCount || 0) + 1));
                                              }}
                                              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                                                ${selectedCount >= availableCount 
                                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                  : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                            >
                                              +
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                // Single duration
                                <div className="p-3 border border-gray-200 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-semibold text-gray-900">{selectedService.duration || 60} min</p>
                                      <p className="text-xs text-gray-500">
                                        {numAssets - (formData.groupSize || 0)} available (up to {assetCapacity} guests per booking)
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.max(1, (formData.groupSize || 1) - 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        −
                                      </button>
                                      <span className="w-10 text-center font-bold text-lg">{formData.groupSize || 0}</span>
                                      <button
                                        type="button"
                                        onClick={() => updateFormData('groupSize', Math.min(numAssets, (formData.groupSize || 0) + 1))}
                                        className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            // Per-Ticket: Show ticket options with REAL-TIME availability
                            <div className="space-y-2">
                              {hasDurationOptions ? (
                                durationOptions.map((duration, idx) => {
                                  // Get real-time availability for this duration
                                  const realTimeData = slotDurations.find(d => Math.abs(d.hours - duration.hours) < 0.1);
                                  const totalCapacity = realTimeData?.available ?? (selectedService?.capacity || 12);
                                  const selectedCount = formData[`tickets_duration_${idx}`] || 0;
                                  const remaining = totalCapacity - selectedCount;
                                  const isSoldOut = totalCapacity === 0;
                                  
                                  return (
                                    <div
                                      key={idx}
                                      className={`
                                        p-3 border rounded-lg transition-all
                                        ${isSoldOut ? 'border-gray-200 bg-gray-50 opacity-60' :
                                          selectedCount > 0 ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
                                      `}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className={`font-semibold ${isSoldOut ? 'text-gray-400' : 'text-gray-900'}`}>
                                            {duration.hours} {duration.hours === 1 ? 'Hour' : 'Hours'} - {formatCurrency(realTimeData?.price || duration.price, selectedService.currency)}
                                          </p>
                                          <p className={`text-xs ${isSoldOut ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                            {isSoldOut ? '❌ SOLD OUT' : `${remaining} tickets available`}
                                          </p>
                                        </div>
                                        {!isSoldOut && (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const current = formData[`tickets_duration_${idx}`] || 0;
                                                const newValue = Math.max(0, current - 1);
                                                
                                                // Calculate new total by summing all duration ticket counts
                                                let newTotal = 0;
                                                durationOptions.forEach((_, i) => {
                                                  if (i === idx) {
                                                    newTotal += newValue;
                                                  } else {
                                                    newTotal += formData[`tickets_duration_${i}`] || 0;
                                                  }
                                                });
                                                
                                                // Update both values
                                                setFormData(prev => ({
                                                  ...prev,
                                                  [`tickets_duration_${idx}`]: newValue,
                                                  groupSize: Math.max(1, newTotal), // Ensure at least 1 for validation
                                                  selectedDurationIndex: newTotal > 0 ? idx : prev.selectedDurationIndex,
                                                  duration: duration.hours * 60
                                                }));
                                              }}
                                              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                            >
                                              −
                                            </button>
                                            <span className="w-10 text-center font-bold text-lg">{selectedCount}</span>
                                            <button
                                              type="button"
                                              disabled={selectedCount >= totalCapacity}
                                              onClick={() => {
                                                const current = formData[`tickets_duration_${idx}`] || 0;
                                                const newValue = Math.min(totalCapacity, current + 1);
                                                
                                                // Calculate new total by summing all duration ticket counts
                                                let newTotal = 0;
                                                durationOptions.forEach((_, i) => {
                                                  if (i === idx) {
                                                    newTotal += newValue;
                                                  } else {
                                                    newTotal += formData[`tickets_duration_${i}`] || 0;
                                                  }
                                                });
                                                
                                                // Update all values in a single state update
                                                setFormData(prev => ({
                                                  ...prev,
                                                  [`tickets_duration_${idx}`]: newValue,
                                                  groupSize: newTotal,
                                                  selectedDurationIndex: idx,
                                                  duration: duration.hours * 60
                                              }));
                                            }}
                                            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                                              ${selectedCount >= totalCapacity 
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                          >
                                            +
                                          </button>
                                        </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                // Single duration with real-time availability
                                (() => {
                                  const realTimeData = slotDurations[0];
                                  const totalCapacity = realTimeData?.available ?? (selectedService?.capacity || 12);
                                  const isSoldOut = totalCapacity === 0;
                                  
                                  return (
                                    <div className={`p-3 border rounded-lg ${isSoldOut ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className={`font-semibold ${isSoldOut ? 'text-gray-400' : 'text-gray-900'}`}>
                                            {selectedService.duration || 60} min
                                          </p>
                                          <p className={`text-xs ${isSoldOut ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                            {isSoldOut ? '❌ SOLD OUT' : `${totalCapacity - (formData.groupSize || 0)} tickets available`}
                                          </p>
                                        </div>
                                        {!isSoldOut && (
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => updateFormData('groupSize', Math.max(1, (formData.groupSize || 1) - 1))}
                                              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 font-bold text-lg"
                                            >
                                              −
                                            </button>
                                            <span className="w-10 text-center font-bold text-lg">{formData.groupSize || 0}</span>
                                            <button
                                              type="button"
                                              disabled={(formData.groupSize || 0) >= totalCapacity}
                                              onClick={() => updateFormData('groupSize', Math.min(totalCapacity, (formData.groupSize || 0) + 1))}
                                              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                                                ${(formData.groupSize || 0) >= totalCapacity 
                                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                  : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                            >
                                              +
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()
                              )}
                            </div>
                          );
                          })()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500">No time slots for this date</p>
                    </div>
                  );
                })()}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 3:
        // Add-ons (optional, skip if no add-ons) - supports both regular and custom bookings
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {entryPoint === 'custom' ? 'Add-ons & Extras' : 'Enhance Your Booking'}
            </h3>

            {/* Custom Booking Add-ons */}
            {entryPoint === 'custom' ? (
              <div className="space-y-4">
                {/* Existing org add-ons */}
                {allOrgAddOns.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">Select from existing add-ons:</p>
                    {allOrgAddOns.map((addon, index) => {
                      const addonKey = addon.id || `org_addon_${index}`;
                      const isSelected = formData.selectedAddOns[addonKey] !== undefined;

                      return (
                        <div
                          key={addonKey}
                          onClick={() => {
                            const newAddOns = { ...formData.selectedAddOns };
                            if (isSelected) {
                              delete newAddOns[addonKey];
                            } else {
                              newAddOns[addonKey] = {
                                id: addonKey,
                                title: addon.title,
                                description: addon.description || '',
                                price: addon.price || 0,
                                type: 'checkbox'
                              };
                            }
                            updateFormData('selectedAddOns', newAddOns);
                          }}
                          className={`
                            p-3 border rounded-lg cursor-pointer transition-all
                            ${isSelected ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'}
                          `}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`
                                w-5 h-5 rounded border flex items-center justify-center flex-shrink-0
                                ${isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}
                              `}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 text-sm">{addon.title}</p>
                                {addon.description && <p className="text-xs text-gray-500">{addon.description}</p>}
                              </div>
                            </div>
                            <p className="font-semibold text-purple-600 text-sm">
                              {addon.price > 0 ? `+${formatCurrency(addon.price)}` : 'Free'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Custom Add-ons Section */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Custom add-ons:</p>
                  
                  {/* List of custom add-ons */}
                  {customBookingData.customAddOns.map((addon, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{addon.description}</p>
                      </div>
                      <p className="text-sm font-semibold text-purple-600">{formatCurrency(addon.amount)}</p>
                      <button
                        onClick={() => {
                          const newCustomAddOns = customBookingData.customAddOns.filter((_, i) => i !== index);
                          setCustomBookingData({...customBookingData, customAddOns: newCustomAddOns});
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {/* Add custom add-on form */}
                  <div className="flex gap-2 mt-3">
                    <Input
                      placeholder="Description (e.g., BBQ Tray)"
                      className="flex-1 text-sm"
                      id="customAddonDesc"
                    />
                    <div className="relative w-24">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="pl-5 text-sm"
                        id="customAddonAmount"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        const descInput = document.getElementById('customAddonDesc');
                        const amountInput = document.getElementById('customAddonAmount');
                        const desc = descInput?.value?.trim();
                        const amount = parseFloat(amountInput?.value) || 0;
                        
                        if (desc && amount > 0) {
                          setCustomBookingData({
                            ...customBookingData,
                            customAddOns: [...customBookingData.customAddOns, { description: desc, amount }]
                          });
                          descInput.value = '';
                          amountInput.value = '';
                        } else {
                          toast.error('Please enter description and amount');
                        }
                      }}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* Total Preview */}
                <div className="bg-purple-50 rounded-lg p-3 mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Base Price:</span>
                    <span className="font-medium">{formatCurrency(parseFloat(customBookingData.basePrice) || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Add-ons:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        Object.values(formData.selectedAddOns).reduce((sum, a) => sum + (a.price || 0), 0) +
                        customBookingData.customAddOns.reduce((sum, a) => sum + (a.amount || 0), 0)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold text-purple-700 mt-2 pt-2 border-t border-purple-200">
                    <span>Subtotal:</span>
                    <span>
                      {formatCurrency(
                        (parseFloat(customBookingData.basePrice) || 0) +
                        Object.values(formData.selectedAddOns).reduce((sum, a) => sum + (a.price || 0), 0) +
                        customBookingData.customAddOns.reduce((sum, a) => sum + (a.amount || 0), 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ) : (selectedService?.addOns && selectedService.addOns.length > 0) || (selectedService?.addons && selectedService.addons.length > 0) ? (
              <div className="space-y-3">
                {(selectedService.addOns || selectedService.addons || []).map((addon, index) => {
                  const addonKey = addon.id || `addon_${index}`;
                  const isSelected = formData.selectedAddOns[addonKey] !== undefined;
                  const addonPrice = addon.type === 'percentage'
                    ? ((selectedService.basePrice || selectedService.price || 0) * (addon.percentage || 0) / 100)
                    : (addon.price || 0);

                  return (
                    <div
                      key={addonKey}
                      onClick={() => {
                        const newAddOns = { ...formData.selectedAddOns };
                        if (isSelected) {
                          delete newAddOns[addonKey];
                        } else {
                          // Store full add-on details including title for display on tickets
                          newAddOns[addonKey] = {
                            id: addonKey,
                            title: addon.title || addon.name || `Add-on ${index + 1}`,
                            description: addon.description || '',
                            price: addonPrice,
                            type: addon.type || 'checkbox'
                          };
                        }
                        updateFormData('selectedAddOns', newAddOns);
                        
                        // Update total amount - now accessing .price from object
                        const addOnsTotal = Object.values(newAddOns).reduce((sum, a) => sum + (a.price || 0), 0);
                        updateFormData('totalAmount', (selectedService.basePrice || selectedService.price || 0) + addOnsTotal);
                      }}
                      className={`
                        p-4 border rounded-lg cursor-pointer transition-all
                        ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
                      `}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`
                            w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5
                            ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}
                          `}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {addon.title || addon.name}
                              {addon.required && <span className="text-red-500 ml-1">*</span>}
                            </p>
                            <ExpandableAddOnDescription description={addon.description} maxLength={80} />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-blue-600">
                            {addonPrice > 0 ? `+${formatCurrency(addonPrice, selectedService?.currency)}` : 'Free'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm text-gray-500">No add-ons available for this service</p>
              </div>
            )}

            {/* Custom Questions Section */}
            {selectedService?.customQuestions && selectedService.customQuestions.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Additional Information
                </h4>
                <div className="space-y-4">
                  {selectedService.customQuestions.map((question, idx) => {
                    const questionKey = `custom_q_${idx}`;
                    const currentValue = formData[questionKey] || '';
                    
                    return (
                      <div key={questionKey}>
                        <Label>
                          {question.question || question.label || `Question ${idx + 1}`}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {question.type === 'select' && question.options ? (
                          <Select
                            value={currentValue}
                            onValueChange={(value) => updateFormData(questionKey, value)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {question.options.map((opt, optIdx) => (
                                <SelectItem key={optIdx} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : question.type === 'textarea' ? (
                          <textarea
                            value={currentValue}
                            onChange={(e) => updateFormData(questionKey, e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            placeholder={question.placeholder || 'Enter your answer...'}
                          />
                        ) : (
                          <Input
                            value={currentValue}
                            onChange={(e) => updateFormData(questionKey, e.target.value)}
                            placeholder={question.placeholder || 'Enter your answer...'}
                            className="mt-1"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        // Customer Details - Simplified
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Details
            </h3>

            <div>
              <Label>Customer Name <span className="text-red-500">*</span></Label>
              <Input
                value={formData.customerName}
                onChange={(e) => updateFormData('customerName', e.target.value)}
                placeholder="John Doe"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Customer Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => updateFormData('customerEmail', e.target.value)}
                placeholder="email@example.com"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label>Customer Phone <span className="text-red-500">*</span></Label>
              <Input
                type="tel"
                value={formData.customerPhone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  updateFormData('customerPhone', value);
                }}
                placeholder="5551234567"
                className="mt-1"
                maxLength={15}
                required
              />
              <p className="text-xs text-gray-400 mt-0.5">Numbers only</p>
            </div>

            <div>
              <Label>Pickup Location</Label>
              <Input
                value={formData.pickupLocation}
                onChange={(e) => updateFormData('pickupLocation', e.target.value)}
                placeholder="Enter pickup location"
                className="mt-1"
              />
              <div className="flex items-center justify-between mt-2">
                <button
                  type="button"
                  onClick={() => {
                    const defaultLocation = localStorage.getItem('defaultPickupLocation') || '';
                    if (defaultLocation) {
                      updateFormData('pickupLocation', defaultLocation);
                      toast.success('Default location applied');
                    } else {
                      toast.error('No default location saved yet');
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Use default location
                </button>
                {formData.pickupLocation && (
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('defaultPickupLocation', formData.pickupLocation);
                      toast.success('Saved as default location');
                    }}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Save as default
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Don&apos;t worry - you can always change this on individual bookings later
              </p>
            </div>
          </div>
        );

      case 5:
        // Review & Confirm - handles both regular and custom bookings
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Review & Confirm
            </h3>

            {/* Pricing Breakdown */}
            {(() => {
              // Handle custom booking pricing
              const isCustom = entryPoint === 'custom';
              const basePrice = isCustom 
                ? parseFloat(customBookingData.basePrice) || 0
                : parseFloat(selectedService?.basePrice || selectedService?.price || 0);
              
              // Regular add-ons
              const regularAddOnsTotal = Object.values(formData.selectedAddOns).reduce((sum, addon) => sum + (addon?.price || 0), 0);
              // Custom add-ons (only for custom bookings)
              const customAddOnsTotal = isCustom 
                ? customBookingData.customAddOns.reduce((sum, addon) => sum + (addon?.amount || 0), 0)
                : 0;
              const addOnsTotal = regularAddOnsTotal + customAddOnsTotal;
              
              const preTotal = basePrice + addOnsTotal;
              const platformFee = (preTotal * 0.059) + 0.30;
              
              // Get tax settings - for custom bookings, use org defaults
              const taxEnabled = isCustom 
                ? true // Custom bookings always show tax
                : (selectedService?.taxEnabled || selectedService?.salesTaxEnabled || false);
              let taxRateRaw = isCustom 
                ? 8.25 // Default tax rate for custom - TODO: get from org settings
                : (selectedService?.taxRate || selectedService?.salesTaxRate || 0);
              // FIXED: taxRate is ALWAYS stored as percentage (e.g., 1 = 1%, 8.25 = 8.25%), ALWAYS divide by 100
              const taxRateDecimal = taxRateRaw / 100;
              const salesTax = taxEnabled ? preTotal * taxRateDecimal : 0;
              const grandTotal = preTotal + platformFee + salesTax;

              const currency = isCustom ? orgCurrency : (selectedService?.currency || orgCurrency);

              return (
                <div className={`${isCustom ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'} border rounded-lg p-4 space-y-2`}>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">{isCustom ? 'Custom Price:' : 'Service Price:'}</span>
                    <span className="font-semibold">{formatCurrency(basePrice, currency)}</span>
                  </div>
                  
                  {addOnsTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Add-ons:</span>
                      <span className="font-semibold">+{formatCurrency(addOnsTotal, currency)}</span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal:</span>
                      <span className="font-semibold">{formatCurrency(preTotal, currency)}</span>
                    </div>
                  </div>

                  {taxEnabled && salesTax > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Tax ({taxRateRaw.toFixed(2)}%):</span>
                      <span className="font-semibold">+{formatCurrency(salesTax, currency)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Platform Fee:</span>
                    <span className="font-semibold">+{formatCurrency(platformFee, currency)}</span>
                  </div>

                  <div className={`border-t ${isCustom ? 'border-purple-300' : 'border-blue-300'} pt-2`}>
                    <div className={`flex justify-between text-lg font-bold ${isCustom ? 'text-purple-700' : 'text-blue-700'}`}>
                      <span>Total Amount:</span>
                      <span>{formatCurrency(grandTotal, currency)}</span>
                    </div>
                  </div>

                  {/* Gift card applied — show negative line + adjusted total */}
                  {appliedGiftCard && (
                    <>
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-indigo-600 flex items-center gap-1.5">
                          Gift card ({appliedGiftCard.code.slice(0, 8)}…)
                        </span>
                        <span className="font-semibold text-indigo-600">
                          −{formatCurrency(appliedGiftCard.applied, currency)}
                        </span>
                      </div>
                      <div className={`border-t ${isCustom ? 'border-purple-300' : 'border-blue-300'} pt-2`}>
                        <div className="flex justify-between text-base font-bold text-emerald-700">
                          <span>Charge today:</span>
                          <span>{formatCurrency(Math.max(0, grandTotal - appliedGiftCard.applied), currency)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Gift Card apply block (operator-side) */}
            <div className="my-3">
              <GiftCardApplyBlock
                apiUrl={BACKEND_URL}
                orgSlug={localStorage.getItem('organizationSlug') || localStorage.getItem('orgSlug') || ''}
                bookingTotal={(() => {
                  const isCustom = entryPoint === 'custom';
                  const basePrice = isCustom
                    ? parseFloat(customBookingData.basePrice) || 0
                    : parseFloat(selectedService?.basePrice || selectedService?.price || 0);
                  const regularAddOnsTotal = Object.values(formData.selectedAddOns || {}).reduce((s, a) => s + (a?.price || 0), 0);
                  const customAddOnsTotal = isCustom
                    ? (customBookingData.customAddOns || []).reduce((s, a) => s + (parseFloat(a?.amount) || 0), 0)
                    : 0;
                  const preTotal = (basePrice * (formData.groupSize || 1)) + regularAddOnsTotal + customAddOnsTotal;
                  const platformFee = (preTotal * 0.059) + 0.30;
                  const taxEnabledFlag = isCustom
                    ? (parseFloat(customBookingData.taxRate) > 0)
                    : (selectedService?.taxEnabled || selectedService?.salesTaxEnabled || false);
                  const taxRate = isCustom
                    ? (parseFloat(customBookingData.taxRate) || 0) / 100
                    : ((selectedService?.taxRate || selectedService?.salesTaxRate || 0) / 100);
                  const salesTax = taxEnabledFlag ? preTotal * taxRate : 0;
                  return preTotal + platformFee + salesTax;
                })()}
                currency={formData.presentmentCurrency || selectedService?.currency || orgCurrency}
                customerEmail={formData.customerEmail}
                applied={appliedGiftCard}
                onApplied={(info) => setAppliedGiftCard(info)}
                onRemoved={() => setAppliedGiftCard(null)}
              />
            </div>

            {/* Booking Summary */}
            <div className="space-y-3 text-sm">
              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  {entryPoint === 'custom' ? 'Custom Service' : 'Service'}
                </p>
                <p className="font-semibold text-gray-900">
                  {entryPoint === 'custom' ? customBookingData.serviceName : selectedService?.name}
                </p>
                {entryPoint === 'custom' && customBookingData.notes && (
                  <p className="text-xs text-gray-600 mt-1">{customBookingData.notes}</p>
                )}
              </div>

              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                <p className="font-semibold text-gray-900">
                  {formatDateTimeHuman(formData.date, formData.startTime, { includeDay: true })}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  🕐 {getTimezoneAbbreviation(orgTimezone)} timezone
                </p>
              </div>

              <div className="border-b pb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                <p className="font-semibold text-gray-900">{formData.customerName}</p>
                <p className="text-xs text-gray-600">{formData.customerEmail}</p>
                <p className="text-xs text-gray-600">{formData.customerPhone}</p>
              </div>

              {formData.pickupLocation && (
                <div className="border-b pb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Location</p>
                  <p className="font-semibold text-gray-900">
                    {formData.pickupLocation}
                  </p>
                </div>
              )}

              {/* Show custom add-ons if any */}
              {entryPoint === 'custom' && customBookingData.customAddOns.length > 0 && (
                <div className="border-b pb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Custom Add-ons</p>
                  {customBookingData.customAddOns.map((addon, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span>{addon.description}</span>
                      <span className="font-medium">{formatCurrency(addon.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        // Payment
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment
            </h3>

            {/* Payment Method Selector - 4 Clean Options */}
            <div className="grid gap-3">
              {/* Option 1: Pay Now (Redirect to Stripe) */}
              <div
                onClick={() => updateFormData('paymentStatus', 'pay_now_redirect')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'pay_now_redirect' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'pay_now_redirect' ? 'bg-blue-600' : 'bg-gray-100'}
                    `}>
                      <CreditCard className={`w-5 h-5 ${formData.paymentStatus === 'pay_now_redirect' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Pay Now</p>
                      <p className="text-xs text-gray-600">Enter card details via Stripe (over phone)</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'pay_now_redirect' && <Check className="w-5 h-5 text-blue-600" />}
                </div>
              </div>

              {/* Option 1.5: Tap to Pay — native app only (Stripe Terminal) */}
              {isTapToPaySupported() && (orgCurrency || 'USD').toUpperCase() !== 'AED' && (
                <div
                  onClick={() => updateFormData('paymentStatus', 'tap_to_pay')}
                  data-testid="tap-to-pay-payment-option"
                  className={`
                    p-4 border rounded-lg cursor-pointer transition-all
                    ${formData.paymentStatus === 'tap_to_pay' ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        ${formData.paymentStatus === 'tap_to_pay' ? 'bg-emerald-600' : 'bg-gray-100'}
                      `}>
                        <Smartphone className={`w-5 h-5 ${formData.paymentStatus === 'tap_to_pay' ? 'text-white' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Tap to Pay</p>
                        <p className="text-xs text-gray-600">Tap their card on this phone — no reader needed</p>
                      </div>
                    </div>
                    {formData.paymentStatus === 'tap_to_pay' && <Check className="w-5 h-5 text-emerald-600" />}
                  </div>
                </div>
              )}

              {/* Option 2: Share Payment Link */}
              <div
                onClick={() => updateFormData('paymentStatus', 'send_link')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'send_link' ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'send_link' ? 'bg-purple-600' : 'bg-gray-100'}
                    `}>
                      <Link2 className={`w-5 h-5 ${formData.paymentStatus === 'send_link' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Share Payment Link</p>
                      <p className="text-xs text-gray-600">Send Stripe link to customer via SMS/Email</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'send_link' && <Check className="w-5 h-5 text-purple-600" />}
                </div>
              </div>
              
              {/* Option 2.5: Secure with Deposit (only if service has deposit configured) */}
              {selectedService && Number(selectedService.depositAmount) > 0 && (
                <div
                  onClick={() => updateFormData('paymentStatus', 'deposit')}
                  data-testid="deposit-payment-option"
                  className={`
                    p-4 border rounded-lg cursor-pointer transition-all
                    ${formData.paymentStatus === 'deposit' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        ${formData.paymentStatus === 'deposit' ? 'bg-blue-600' : 'bg-gray-100'}
                      `}>
                        <Lock className={`w-5 h-5 ${formData.paymentStatus === 'deposit' ? 'text-white' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Secure with Deposit</p>
                        <p className="text-xs text-gray-600">
                          Charge {selectedService.depositType === 'percentage' 
                            ? `${selectedService.depositAmount}% deposit` 
                            : `$${selectedService.depositAmount} deposit`} — collect balance later
                        </p>
                      </div>
                    </div>
                    {formData.paymentStatus === 'deposit' && <Check className="w-5 h-5 text-blue-600" />}
                  </div>
                </div>
              )}

              {/* Option 3: Already Paid */}
              <div
                onClick={() => updateFormData('paymentStatus', 'paid')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'paid' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'paid' ? 'bg-green-600' : 'bg-gray-100'}
                    `}>
                      <Check className={`w-5 h-5 ${formData.paymentStatus === 'paid' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Already Paid</p>
                      <p className="text-xs text-gray-600">Payment collected externally (cash, check, etc.)</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'paid' && <Check className="w-5 h-5 text-green-600" />}
                </div>
              </div>

              {/* Option 4: Will Pay Later */}
              <div
                onClick={() => updateFormData('paymentStatus', 'unpaid')}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-all
                  ${formData.paymentStatus === 'unpaid' ? 'border-yellow-600 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${formData.paymentStatus === 'unpaid' ? 'bg-yellow-600' : 'bg-gray-100'}
                    `}>
                      <Clock className={`w-5 h-5 ${formData.paymentStatus === 'unpaid' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Will Pay Later</p>
                      <p className="text-xs text-gray-600">Deposit, on arrival, invoice, etc.</p>
                    </div>
                  </div>
                  {formData.paymentStatus === 'unpaid' && <Check className="w-5 h-5 text-yellow-600" />}
                </div>
              </div>
            </div>

            {/* Multi-Currency Selector - Only for Stripe payment options */}
            {(formData.paymentStatus === 'pay_now_redirect' || formData.paymentStatus === 'send_link') && supportedCurrencies.length > 0 && (
              <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">💱</span>
                  <h4 className="font-semibold text-gray-800">Customer&apos;s Currency</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Charge in a different currency for international customers
                </p>
                
                {/* Native currency for this booking */}
                {(() => {
                  const isCustom = entryPoint === 'custom';
                  const nativeCurrency = isCustom ? orgCurrency : (selectedService?.currency || orgCurrency);
                  return (
                    <>
                      <select
                        value={formData.presentmentCurrency || nativeCurrency.toLowerCase()}
                        onChange={(e) => updateFormData('presentmentCurrency', e.target.value)}
                        className="w-full p-3 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {/* Default - Service/Org Currency (No conversion) */}
                        <option value={nativeCurrency.toLowerCase()}>
                          {supportedCurrencies.find(c => c.code === nativeCurrency.toUpperCase())?.flag || '🏳️'} {nativeCurrency.toUpperCase()} - Default (No conversion)
                        </option>
                        
                        {/* All currencies alphabetically by ISO code */}
                        {supportedCurrencies
                          .filter(c => c.code.toLowerCase() !== nativeCurrency.toLowerCase())
                          .sort((a, b) => a.code.localeCompare(b.code))
                          .map(currency => (
                            <option key={currency.code} value={currency.code.toLowerCase()}>
                              {currency.flag} {currency.code} - {currency.name}
                            </option>
                          ))
                        }
                      </select>
                      
                      {/* Converted Price Display */}
                      {loadingConversion && (
                        <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                          <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                          Calculating conversion...
                        </div>
                      )}
                      
                      {convertedPrice && !loadingConversion && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Original Total:</span>
                            <span className="font-medium">
                              {convertedPrice.fromCurrency} {convertedPrice.originalAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-sm text-gray-600">Rate (incl. 3% fee):</span>
                            <span className="text-sm">
                              1 {convertedPrice.fromCurrency} = {convertedPrice.rateWithFee.toFixed(4)} {convertedPrice.toCurrency}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
                            <span className="font-semibold text-gray-800">Customer Pays:</span>
                            <span className="text-lg font-bold text-blue-600">
                              {supportedCurrencies.find(c => c.code === convertedPrice.toCurrency)?.symbol || convertedPrice.toCurrency} {convertedPrice.convertedAmount.toFixed(2)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            💡 Stripe converts to {nativeCurrency.toUpperCase()} when settling to your account
                          </p>
                        </div>
                      )}
                      
                      {!formData.presentmentCurrency || formData.presentmentCurrency === nativeCurrency.toLowerCase() ? (
                        <p className="text-xs text-gray-500 mt-2">
                          Customer will be charged in {nativeCurrency.toUpperCase()} (your default currency)
                        </p>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 0,
          padding: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: widgetExpanded ? 100 : 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onCancel();
          }
        }}
      >
        <div 
          className={widgetExpanded
            ? 'bg-white w-full h-full flex flex-col'
            : 'bg-white flex flex-col max-sm:w-full max-sm:h-full max-sm:pt-[env(safe-area-inset-top,0px)] sm:m-4 sm:rounded-xl sm:shadow-2xl sm:max-w-2xl sm:w-full sm:max-h-[90vh]'}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header — hidden while the widget's own checkout modal runs full-viewport */}
        {!widgetExpanded && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Add Booking</h2>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition" data-testid="add-booking-close-btn">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Continuous Progress Bar — hidden while the embedded widget runs its own flow */}
          {entryPoint !== 'quick' && !(entryPoint === 'service' && currentStep === 1) && (
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                style={{ width: `${(getStep2Progress() / totalSteps) * 100}%` }}
              />
            </div>
          )}
        </div>
        )}

        {/* Content */}
        <div className={widgetExpanded ? 'flex-1 overflow-hidden' : 'flex-1 overflow-y-auto p-6'}>
          {renderStepContent()}
        </div>

        {/* Quick Add footer — single action, no wizard Back/Next */}
        {entryPoint === 'quick' ? (
        <div className="p-6 border-t">
          <Button
            onClick={() => quickAddRef.current?.submit()}
            disabled={quickSubmit.submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-11"
            data-testid="quick-add-submit"
          >
            {quickSubmit.submitting
              ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {quickSubmit.mode === 'link' ? 'Adding & generating link…' : quickSubmit.mode === 'tap' ? 'Adding & collecting payment…' : 'Adding booking…'}</>
              : <><Zap className="w-4 h-4 mr-2" /> Add booking</>}
          </Button>
        </div>
        ) : !(entryPoint === 'service' && currentStep === 1) && (
        <div className="p-6 border-t flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          {currentStep === totalSteps ? (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.paymentStatus}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2 text-white font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating Booking...
                </>
              ) : (
                <>
                  Confirm & Create Booking
                  <Check className="w-4 h-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && createdBooking && (
        <>
          {/* Confetti Animation */}
          <div className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2.5 h-2.5 animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: '-10px',
                  backgroundColor: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'][i % 6],
                  borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                  animation: `confetti-fall ${2 + Math.random() * 2}s ease-out ${Math.random() * 0.5}s forwards`,
                }}
              />
            ))}
          </div>
          <style>{`
            @keyframes confetti-fall {
              0% { transform: translateY(0) rotate(0deg); opacity: 1; }
              100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
            }
          `}</style>
          
          {/* Success modal content */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: 0,
              padding: '16px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 70,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={async () => {
              setShowSuccessModal(false);
              onCancel();
              if (window.refreshBookingData) {
                await window.refreshBookingData();
              }
            }}
          >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div className={`p-8 text-center ${
              createdBooking.paymentStatus === 'paid' 
                ? 'bg-gradient-to-br from-emerald-500 to-green-600' 
                : createdBooking.paymentStatus === 'pending'
                ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                : 'bg-gradient-to-br from-amber-500 to-orange-600'
            }`}>
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Check className={`w-12 h-12 ${
                  createdBooking.paymentStatus === 'paid' ? 'text-emerald-600' :
                  createdBooking.paymentStatus === 'pending' ? 'text-blue-600' :
                  'text-amber-600'
                }`} strokeWidth={2.5} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {createdBooking.paymentStatus === 'paid' ? 'Booking Confirmed!' :
                 createdBooking.paymentLink ? 'Payment Link Ready!' :
                 createdBooking.paymentStatus === 'pending' && !createdBooking.quickAdd ? 'Payment Link Sent!' :
                 'Booking Created!'}
              </h2>
              <p className="text-white/90 text-sm">
                {createdBooking.paymentStatus === 'paid' ? 'Payment received • Customer notified' :
                 createdBooking.paymentLink ? 'Copy the link below and share it your way • Awaiting payment' :
                 createdBooking.paymentStatus === 'pending' && !createdBooking.quickAdd ? 'Link sent to customer • Awaiting payment' :
                 createdBooking.paymentStatus === 'pending' ? 'Awaiting payment' :
                 createdBooking.paymentStatus === 'unpaid' ? 'Payment to be collected later' :
                 'Customer added to Relations'}
              </p>
            </div>

            {/* Booking Details */}
            <div className="p-6 space-y-4">
              {/* Confirmation Code */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 font-medium">Confirmation Code</p>
                <p className="text-3xl font-mono font-bold text-gray-900 tracking-wider">{createdBooking.confirmationCode}</p>
              </div>

              {/* 💳 Quick Add payment link — generated, never auto-sent. Open it or copy it. */}
              {createdBooking.paymentLink && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="quick-add-payment-link-box">
                  <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold mb-2">Payment Link</p>
                  <p className="text-xs font-mono text-gray-700 bg-white border border-blue-100 rounded-lg px-3 py-2 truncate" data-testid="quick-add-payment-link-url">{createdBooking.paymentLink}</p>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button
                      variant="outline"
                      onClick={() => { navigator.clipboard.writeText(createdBooking.paymentLink); toast.success('Payment link copied!'); }}
                      className="w-full border-blue-200 text-blue-700 hover:bg-blue-100"
                      data-testid="quick-add-copy-payment-link"
                    >
                      <Link2 className="w-4 h-4 mr-2" /> Copy link
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open(createdBooking.paymentLink, '_blank')}
                      className="w-full border-blue-200 text-blue-700 hover:bg-blue-100"
                      data-testid="quick-add-open-payment-link"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> Open
                    </Button>
                  </div>
                  <p className="text-[11px] text-blue-600/80 mt-2">Share it however you like — the booking flips to paid automatically once they pay.</p>
                </div>
              )}

              {/* Service Name - Prominently displayed */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Service</p>
                <p className="text-lg font-semibold text-gray-900">{createdBooking.serviceName || 'Custom Booking'}</p>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Customer</p>
                  <p className="text-sm font-semibold text-gray-900">{createdBooking.customerName}</p>
                  {createdBooking.customerEmail && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3" />
                      {createdBooking.customerEmail}
                    </p>
                  )}
                  {createdBooking.customerPhone && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />
                      {createdBooking.customerPhone}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Date & Time</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDateHuman(createdBooking.preferredDate || createdBooking.date, { includeDay: true })}
                  </p>
                  <p className="text-sm text-gray-900">
                    {formatTimeHuman(createdBooking.preferredTime || createdBooking.startTime)} <span className="text-gray-500">{getTimezoneAbbreviation(createdBooking.timezone || orgTimezone)}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{createdBooking.duration || 60} min</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Party Size</p>
                  <p className="text-sm font-semibold text-gray-900">{createdBooking.groupSize} {createdBooking.groupSize === 1 ? 'guest' : 'guests'}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Asset</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {createdBooking.asset?.name || <span className="text-gray-400 italic">Not assigned</span>}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-medium">Team</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {createdBooking.teamAssignments?.length > 0 
                      ? `${createdBooking.teamAssignments.length} assigned` 
                      : <span className="text-gray-400 italic">Not assigned</span>}
                  </p>
                </div>
              </div>

              {/* Payment Summary */}
              {createdBooking.totalAmount > 0 && (
                <div className={`rounded-xl p-4 ${
                  createdBooking.paymentStatus === 'paid' ? 'bg-emerald-50 border border-emerald-200' :
                  createdBooking.paymentStatus === 'pending' ? 'bg-blue-50 border border-blue-200' :
                  'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs uppercase tracking-wide font-medium ${
                      createdBooking.paymentStatus === 'paid' ? 'text-emerald-700' :
                      createdBooking.paymentStatus === 'pending' ? 'text-blue-700' :
                      'text-amber-700'
                    }`}>
                      {createdBooking.paymentStatus === 'paid' ? '✓ Payment Received' :
                       createdBooking.paymentStatus === 'pending' ? '⏳ Awaiting Payment' :
                       '⚡ Pay Later'}
                    </span>
                  </div>
                  {(() => {
                    const subtotal = parseFloat(createdBooking.totalAmount);
                    const processingFee = (subtotal * 0.059) + 0.30;
                    const customerPays = subtotal + processingFee;
                    const currency = createdBooking.currency || selectedService?.currency || 'USD';
                    
                    return (
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Subtotal</span>
                          <span>{formatCurrency(subtotal, currency)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Processing Fee</span>
                          <span>+{formatCurrency(processingFee, currency)}</span>
                        </div>
                        <div className={`flex justify-between text-lg font-bold pt-2 border-t ${
                          createdBooking.paymentStatus === 'paid' ? 'text-emerald-700 border-emerald-200' :
                          createdBooking.paymentStatus === 'pending' ? 'text-blue-700 border-blue-200' :
                          'text-amber-700 border-amber-200'
                        }`}>
                          <span>Total</span>
                          <span>{formatCurrency(customerPays, currency)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-2 pt-2">
                {/* Copy Link Button */}
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      // Use magic token from booking response if available
                      const magicToken = createdBooking.magicToken;
                      const link = magicToken 
                        ? `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}?token=${magicToken}`
                        : `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}`;
                      navigator.clipboard.writeText(link);
                      toast.success('Ticket link copied!');
                    } catch (err) {
                      const link = `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}`;
                      navigator.clipboard.writeText(link);
                      toast.success('Ticket link copied!');
                    }
                  }}
                  className="w-full"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Ticket Link
                </Button>

                {/* Add to Calendar Button */}
                <Button
                  variant="outline"
                  onClick={() => {
                    // Use webcal for automatic calendar subscription
                    const baseUrl = BACKEND_URL.replace('http://', '').replace('https://', '');
                    const calendarUrl = `webcal://${baseUrl}/api/customer/${createdBooking.confirmationCode}/calendar-feed.ics`;
                    window.location.href = calendarUrl;
                    toast.success('Opening calendar app to sync your booking...');
                  }}
                  className="w-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Sync to Calendar
                </Button>

                {/* Show email/sms buttons if contact info provided */}
                {(createdBooking.customerEmail || createdBooking.customerPhone) && (
                  <div className="grid grid-cols-2 gap-2">
                    {createdBooking.customerEmail && (
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            // Use magic token from booking response
                            const magicToken = createdBooking.magicToken;
                            let ticketLink = `${window.location.origin}/customer-ticket/${createdBooking.confirmationCode}`;
                            if (magicToken) {
                              ticketLink += `?token=${magicToken}`;
                            }
                            
                            // Use preferredDate consistently (not 'date' which may not exist)
                            const bookingDate = createdBooking.preferredDate || createdBooking.date || createdBooking.bookingDate;
                            const formattedDate = bookingDate ? new Date(bookingDate + 'T00:00:00').toLocaleDateString() : 'TBD';
                            await api.post('/emails/send-to-customer', {
                              email: createdBooking.customerEmail,
                              name: createdBooking.customerName,
                              subject: `Your Booking Confirmation - ${createdBooking.confirmationCode}`,
                              message: `Hi ${createdBooking.customerName},\n\nYour booking is confirmed!\n\nConfirmation Code: ${createdBooking.confirmationCode}\nDate: ${formattedDate}\nTime: ${createdBooking.startTime} ${getTimezoneAbbreviation(orgTimezone)}\n\nView your ticket: ${ticketLink}\n\nSee you soon!`
                            });
                            toast.success('Email sent with magic link!');
                          } catch (error) {
                            toast.error('Failed to send email');
                          }
                        }}
                        className="flex-1"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                    )}
                    {createdBooking.customerPhone && (
                      <Button
                        variant="outline"
                        disabled
                        className="flex-1 opacity-50 cursor-not-allowed"
                        title="SMS requires A2P 10DLC verification"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        SMS (Coming Soon)
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={async () => {
                  
                  setShowSuccessModal(false);
                  onCancel(); // Close AddBookingWizard
                  
                  // Trigger data refresh in parent CalendarScheduler
                  if (window.refreshBookingData) {
                    await window.refreshBookingData();
                  }
                  
                  toast.success('Booking created successfully!');
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Share Payment Link Modal */}
      {showShareLinkModal && paymentLinkData && (
        <SharePaymentLinkModal
          open={showShareLinkModal}
          onClose={() => {
            setShowShareLinkModal(false);
            onComplete(); // Close wizard and refresh bookings
          }}
          checkoutUrl={paymentLinkData.checkoutUrl}
          customerEmail={paymentLinkData.customerEmail}
          customerPhone={paymentLinkData.customerPhone}
          bookingDetails={paymentLinkData.bookingDetails}
        />
      )}
    </div>
    </>
  );
};

export default AddBookingWizard;
AddBookingWizard;
